import { useState, useEffect, useRef, useCallback } from "react";
import { css } from "glamor";
import { authFetch } from "../auth";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

// Extracted Components
import { KPICard } from "./plh/KPICard";
import "./plh/plh-kpi.css";
// import { PostHogApiTile } from "./plh/PostHogApiTile";
// import { PostHogIframeTile } from "./plh/PostHogIframeTile";
import { ActivationFunnelTile, FunnelStepPanelPayload } from "./plh/ActivationFunnelTile";
import { ActivationVariablesTile } from "./plh/ActivationVariablesTile";
import { NewSignupsTile } from "./plh/NewSignupsTile";
import { FunnelTrendTile, FunnelTrendData } from "./plh/FunnelTrendTile";
import {
  PLH_LEADING_KPI_CARD_TOOLTIPS,
  PLH_KPI_DETAIL,
  PLH_KPI_DETAIL_MONTH,
  PLH_KPI_ACTIONS,
  PLH_FUNNEL_STEP_DESCRIPTION,
  PLH_SECTION_VISIBILITY,
  PLH_KPI_VISIBILITY,
  PLH_INLINE_CHAT_VISIBILITY,
} from "./plh/plhTileInfo";
import { SortableSection } from "./plh/SortableSection";
import { TopBarTile } from "./plh/TopBarTile";
import { AiBriefTile } from "./plh/AiBriefTile";
import { DiagnosisTile } from "./plh/DiagnosisTile";
import { InlineChatBlock } from "./plh/InlineChatBlock";
import { MissionsTile } from "./plh/MissionsTile";
import { IcpAnalysisTile, DEFAULT_ICP_TARGET, type IcpTarget } from "./plh/IcpAnalysisTile";
import { PLH_MISSIONS_MOCK, type MissionItem } from "./plh/plhMissionsMock";
import { AiCoachChatPanel } from "./plh/AiCoachChatPanel";
import { MaterializationStatusTile, MaterializationViewStatus } from "./plh/MaterializationStatusTile";
import { PostHogEndpointCacheTile } from "./plh/PostHogEndpointCacheTile";

function buildLast6MonthLabels(year: number, month: number): string[] {
  const labels: string[] = [];
  let y = year;
  let m = month;
  for (let i = 0; i < 6; i++) {
    const d = new Date(y, m - 1, 1);
    labels.unshift(d.toLocaleString("en-US", { month: "short", year: "2-digit" }));
    m -= 1;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
  }
  return labels;
}

function previousMonthShort(year: number, month: number): string {
  let m = month - 1;
  let y = year;
  if (m < 1) {
    m = 12;
    y -= 1;
  }
  return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "short" });
}

interface ActivationParameters {
  minUsers: number;
  minHours: number;
  minDays: number;
  minFutureHours: number;
}

interface NewSignup {
  userId: string;
  teamId: string;
  userName: string;
  userEmail: string;
  companyName: string;
  status: "Activated" | "Schedule created" | "Invited teammates" | "Not activated";
  signedUpAt: string;
  source: "organic" | "paid" | "referral" | "direct";
  landing: string;
  referrer: string;
  utm: string;
  country: string;
  context: string;
  signUpReason: string;
  teamUserCount: number;
  assignedPeople: number;
  scheduledDays: number;
  scheduledHours: number;
  futureHours: number;
}

interface PLHData {
  meta: {
    currentWeek: number;
    currentYear: number;
    selectedWeek: number;
    selectedYear: number;
    selectedWeekStart: string;
    selectedWeekEnd: string;
    granularity?: "week" | "month";
    selectedMonth?: number | null;
    selectedCalendarYear?: number | null;
    selectedMonthStart?: string | null;
    selectedMonthEnd?: string | null;
  };
  activationRate: number;
  activationRatePrev: number;
  timeToActivation: number | null;
  timeToActivationSparkline?: number[];
  activatedToPaid: number;
  activatedToPaidHistory?: {
    current: number;
    prevWeek: number | null;
    avg6Weeks: number | null;
  };
  paidFromActivatedCohortHistory?: {
    current: number;
    prevWeek: number | null;
    avg6Weeks: number | null;
  };
  weeklyActiveTeams: {
    count: number;
    rate: number | null;
  };
  newActivatedAccounts: number;
  funnel: {
    webVisits: number;
    icpSignups: number;
    signupReasonNotNull: number;
    onboardingStarted: number;
    scheduleGenerated: number;
    schedulePublished: number;
    activated: number;
    activationRatePercent: number;
    history?: {
      webVisits: { current: number; prevWeek: number | null; avg6Weeks: number | null };
      icpSignups: { current: number; prevWeek: number | null; avg6Weeks: number | null };
      signupReasonNotNull: { current: number; prevWeek: number | null; avg6Weeks: number | null };
      onboardingStarted: { current: number; prevWeek: number | null; avg6Weeks: number | null };
      scheduleGenerated: { current: number; prevWeek: number | null; avg6Weeks: number | null };
      schedulePublished: { current: number; prevWeek: number | null; avg6Weeks: number | null };
      activated: { current: number; prevWeek: number | null; avg6Weeks: number | null };
    };
  };
  newSignups: {
    signups: NewSignup[];
    count: number;
  };
  funnelTrend?: FunnelTrendData;
  totalScheduledHours: number;
  totalScheduledHoursPrev: number;
  totalScheduledHoursSparkline?: number[];
  totalNewUsers: number;
  totalNewUsersPrev: number;
  avgUsersPerTeam: number;
  totalNewUsersSparkline?: number[];
  totalNewUsersAvg6Wk?: number;
  activationParameters: ActivationParameters;
  materializationStatus?: MaterializationViewStatus[];
  posthogEndpointFreshness?: Array<{
    endpoint: string;
    is_cached?: boolean | null;
    last_refresh?: string | null;
    extra?: Record<string, unknown>;
  }>;
}

const DEFAULT_SECTION_ORDER = [
  "ai-brief",
  "diagnosis",
  "kpi-grid",
  "activation-funnel",
  "funnel-trend",
  "missions",
  "icp-analysis",
  "new-signups",
  "activation-variables",
];

/** Section ids removed from the dashboard (e.g. moved to Settings); strip from persisted order. */
const STRIPPED_SECTION_IDS = new Set(["materialization-status"]);

function sanitizeSectionOrder(order: string[]): string[] {
  return order.filter((id) => !STRIPPED_SECTION_IDS.has(id));
}

/** Shape of each KPI card in the Leading Indicators grid (and panel payload). */
interface KpiCardConfig {
  id: string;
  title: string;
  tooltip?: string;
  value: React.ReactNode;
  valueUnit?: string;
  tone: "red" | "green" | "gray";
  badge: "off" | "on" | "na";
  compare?: { prevLabel: string; wk5: { value: string; up?: boolean }; avg: string };
  subRows?: Array<{ label: string; value: string; variant?: "tgt" | "up" | "dn" }>;
  sparkline?: number[];
  sparklineLabels?: string[];
  targetValue?: number;
}

interface ProductLedHealthProps {
  parentTheme?: "light" | "dark";
  onThemeToggle?: () => void;
}

export default function ProductLedHealth({ parentTheme, onThemeToggle }: ProductLedHealthProps = {}) {
  const [filterType] = useState<"ICP" | "all">("ICP");
  const [data, setData] = useState<PLHData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Selected KPI card for the sliding detail panel; null when panel is closed. */
  const [selectedKpiCard, setSelectedKpiCard] = useState<KpiCardConfig | null>(null);
  /** Funnel step detail panel; null when closed. */
  const [funnelStepPanel, setFunnelStepPanel] = useState<FunnelStepPanelPayload | null>(null);
  /** Editable weekly goals per funnel step key (persisted in state and localStorage). */
  const [funnelStepGoals, setFunnelStepGoals] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem("plh-funnel-goals");
      if (!saved) return {};
      const parsed = JSON.parse(saved) as unknown;
      if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) return {};
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof k === "string" && typeof v === "number" && Number.isFinite(v)) out[k] = v;
      }
      return out;
    } catch {
      return {};
    }
  });
  /** Edit history per funnel step key. */
  const [funnelStepEditHistory, setFunnelStepEditHistory] = useState<
    Record<string, Array<{ who: string; value: number; when: string; note?: string }>>
  >({});
  /** Goal mode per funnel step key: 'absolute' for raw numbers, 'percent' for % of previous step. */
  const [funnelStepGoalModes, setFunnelStepGoalModes] = useState<Record<string, "absolute" | "percent">>({});
  /** KPI panel: editable targets per card id (e.g. "5%", "800"). */
  const [kpiGoals, setKpiGoals] = useState<Record<string, string>>({});
  /** KPI panel: edit history per card id. */
  const [kpiEditHistory, setKpiEditHistory] = useState<
    Record<string, Array<{ who: string; value: string; when: string }>>
  >({});
  const [missionCompletedSet, setMissionCompletedSet] = useState<Set<number>>(new Set());
  const [missionNotes, setMissionNotes] = useState<Record<number, string>>({});
  const [selectedMissionIndex, setSelectedMissionIndex] = useState<number | null>(null);
  const [icpTarget, setIcpTarget] = useState<IcpTarget>(DEFAULT_ICP_TARGET);
  const [selectedPanel, setSelectedPanel] = useState<"icp" | "signupProfile" | null>(null);
  const panelCloseRef = useRef<HTMLButtonElement>(null);

  // Section order state with localStorage persistence (merge in any new section ids not in saved)
  const [sectionOrder, setSectionOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("plh-dashboard-order");
      const savedOrder: string[] = saved ? sanitizeSectionOrder(JSON.parse(saved)) : [];
      const missing = DEFAULT_SECTION_ORDER.filter((id) => !savedOrder.includes(id));
      const merged = missing.length ? [...missing, ...savedOrder] : savedOrder.length ? savedOrder : DEFAULT_SECTION_ORDER;
      return sanitizeSectionOrder(merged);
    } catch {
      return DEFAULT_SECTION_ORDER;
    }
  });

  const theme = parentTheme ?? "light";
  /** Week param for API (e.g. "2026-W06"); null = backend default. */
  const [weekParam, setWeekParam] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<"week" | "month">("week");
  /** `YYYY-MM`; null = backend default (last completed month). */
  const [monthParam, setMonthParam] = useState<string | null>(null);
  const [aiCoachOpen, setAiCoachOpen] = useState(false);
  const [plhView, setPlhView] = useState<"dashboard" | "settings">("dashboard");

  // Configure drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor)
  );


  const handleGranularityChange = useCallback((g: "week" | "month") => {
    setGranularity(g);
    if (g === "week") setMonthParam(null);
    else setWeekParam(null);
  }, []);

  const loadPlh = useCallback(
    async (opts?: { posthogForce?: string[] }) => {
      setLoading(true);
      setError(null);
      try {
        const icpOnly = filterType === "ICP";
        let domain = import.meta.env.VITE_PLH_API_URL || "";
        if (domain.endsWith("/")) {
          domain = domain.slice(0, -1);
        }

        let backendUrl = `${domain}/plh?icpOnly=${icpOnly}&granularity=${encodeURIComponent(granularity)}`;
        if (granularity === "week") {
          if (weekParam) backendUrl += `&week=${encodeURIComponent(weekParam)}`;
        } else if (monthParam) {
          backendUrl += `&month=${encodeURIComponent(monthParam)}`;
        }
        const force = opts?.posthogForce;
        if (force?.length) {
          for (const name of force) {
            backendUrl += `&posthogForce=${encodeURIComponent(name)}`;
          }
        }

        const response = await authFetch(backendUrl);

        if (!response.ok) {
          throw new Error(`Error fetching data: ${response.statusText}`);
        }

        const jsonData = await response.json();
        console.log("PLH Data Fetched:", jsonData);
        setData(jsonData);
      } catch (err) {
        console.error("Error fetching PLH data:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    },
    [filterType, weekParam, monthParam, granularity]
  );

  useEffect(() => {
    void loadPlh();
  }, [loadPlh]);

  // Focus close button when KPI panel opens
  useEffect(() => {
    if (selectedKpiCard != null) {
      panelCloseRef.current?.focus();
    }
  }, [selectedKpiCard]);

  // Persist funnel step goals to localStorage so targets are remembered across sessions
  useEffect(() => {
    try {
      localStorage.setItem("plh-funnel-goals", JSON.stringify(funnelStepGoals));
    } catch {
      // ignore quota or other storage errors
    }
  }, [funnelStepGoals]);

  const closeKpiPanel = useCallback(() => setSelectedKpiCard(null), []);
  const closeFunnelStepPanel = useCallback(() => setFunnelStepPanel(null), []);
  const closeMissionPanel = useCallback(() => setSelectedMissionIndex(null), []);
  const closeIcpPanel = useCallback(() => setSelectedPanel(null), []);
  const isPanelOpen =
    selectedKpiCard != null ||
    funnelStepPanel != null ||
    selectedMissionIndex != null ||
    selectedPanel != null;
  const closePanel = useCallback(() => {
    closeKpiPanel();
    closeFunnelStepPanel();
    closeMissionPanel();
    closeIcpPanel();
  }, [closeKpiPanel, closeFunnelStepPanel, closeMissionPanel, closeIcpPanel]);
  useEffect(() => {
    if (!isPanelOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePanel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isPanelOpen, closePanel]);

  const funnel = data?.funnel || {
    webVisits: 0,
    icpSignups: 0,
    signupReasonNotNull: 0,
    onboardingStarted: 0,
    scheduleGenerated: 0,
    schedulePublished: 0,
    activated: 0,
    activationRatePercent: 0,
    history: undefined,
  };

  // Default targets for KPI cards (used when no custom goal is set)
  const defaultKpiTargets: Record<string, string> = {
    "activation-rate": "5%",
    "time-to-activation": "< 30 min",
    "total-scheduled-hours": "800 hrs",
    "total-new-users": "40",
  };

  // Default numeric targets for sparkline baseline
  const defaultKpiTargetValues: Record<string, number> = {
    "activation-rate": 5,
    "time-to-activation": 30,
    "total-scheduled-hours": 800,
    "total-new-users": 40,
  };

  // Parse target string to numeric value (extracts first number from string)
  const parseTargetValue = (targetStr: string): number | undefined => {
    const match = targetStr.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : undefined;
  };

  // Build KPI cards config from data (matches mock DOM structure)
  const kpiCards: KpiCardConfig[] = (() => {
    const actRate = funnel.activationRatePercent;
    const tta = data?.timeToActivation ?? null;
    const totalHours = data?.totalScheduledHours ?? 0;
    const totalUsers = data?.totalNewUsers ?? 0;

    const actRateVal = actRate.toFixed(1);
    const actRatePrevVal = (data?.activationRatePrev ?? 0).toFixed(1);
    const ttaValStr = tta == null ? "∞" : tta.toFixed(1);
    const totalHoursStr = totalHours.toFixed(0);
    const totalHoursPrevStr = (data?.totalScheduledHoursPrev ?? 0).toFixed(0);
    const totalUsersStr = totalUsers.toFixed(0);
    const totalUsersPrevStr = (data?.totalNewUsersPrev ?? 0).toFixed(0);
    const avgUsersPerTeamStr = (data?.avgUsersPerTeam ?? 0).toFixed(1);
    // Helper: build last-6-weeks labels ending at selectedWeek (or generic W1-W6 if missing)
    const buildLast6WeekLabels = (): string[] => {
      const selectedWeek = data?.meta?.selectedWeek;
      if (!selectedWeek || selectedWeek <= 0) {
        return Array.from({ length: 6 }, (_, i) => `W${i + 1}`);
      }
      const startWeek = Math.max(1, selectedWeek - 5);
      const labels: string[] = [];
      for (let wk = startWeek; wk <= selectedWeek; wk += 1) {
        labels.push(`W${wk}`);
      }
      // Ensure exactly 6 labels; if fewer (near year start), left-pad with generic ones
      while (labels.length < 6) {
        labels.unshift(`W${labels.length ? parseInt(labels[0].slice(1), 10) - 1 : 0}`);
      }
      return labels.slice(-6);
    };
    const last6WeekLabels = buildLast6WeekLabels();

    const isMonthView = granularity === "month";
    const leadingKpiTips = PLH_LEADING_KPI_CARD_TOOLTIPS[isMonthView ? "month" : "week"];
    const last6SparklineLabels = isMonthView
      ? buildLast6MonthLabels(
          data?.meta?.selectedCalendarYear ?? new Date().getFullYear(),
          data?.meta?.selectedMonth ?? new Date().getMonth() + 1
        )
      : last6WeekLabels;

    const prevPeriodLabel = isMonthView
      ? data?.meta?.selectedCalendarYear != null && data?.meta?.selectedMonth != null
        ? previousMonthShort(data.meta.selectedCalendarYear, data.meta.selectedMonth)
        : "Prev mo"
      : data?.meta?.selectedWeek && data.meta.selectedWeek > 1
        ? `W${data.meta.selectedWeek - 1}`
        : "Prev wk";

    // TODO: Avg still uses current value as placeholder; previous-week uses real values where available
    const mkCompare = (prevVal: string, avgVal: string, up?: boolean) => ({
      prevLabel: prevPeriodLabel,
      wk5: { value: prevVal, up },
      avg: avgVal,
    });

    // Helper to get target display string (user-set goal or default)
    const getTarget = (id: string) => kpiGoals[id] ?? defaultKpiTargets[id] ?? "—";

    // Helper to get numeric target value for sparkline
    const getTargetValue = (id: string): number | undefined => {
      if (kpiGoals[id]) {
        return parseTargetValue(kpiGoals[id]);
      }
      return defaultKpiTargetValues[id];
    };

    return [
      {
        id: "activation-rate",
        title: "Activation Rate",
        tooltip: leadingKpiTips["activation-rate"],
        value: actRateVal,
        valueUnit: "%",
        tone: actRate >= 40 ? "green" : actRate >= 25 ? "red" : "red",
        badge: actRate >= 40 ? "on" : "off",
        compare: mkCompare(`${actRatePrevVal}%`, `${actRateVal}%`),
        subRows: [{ label: "Target", value: getTarget("activation-rate"), variant: "tgt" }],
        targetValue: getTargetValue("activation-rate"),
      },
      {
        id: "time-to-activation",
        title: "Time to Activation",
        tooltip: leadingKpiTips["time-to-activation"],
        value: ttaValStr,
        valueUnit: tta == null ? undefined : "m",
        tone: tta == null ? "red" : tta <= 60 ? "green" : tta <= 1440 ? "red" : "red",
        badge: tta == null ? "off" : tta <= 60 ? "on" : "off",
        // TTA: keep placeholder behaviour (no dedicated previous-week metric yet)
        compare: mkCompare(ttaValStr, ttaValStr),
        sparkline: data?.timeToActivationSparkline ?? [],
        sparklineLabels: last6SparklineLabels,
        subRows: [{ label: "Target", value: getTarget("time-to-activation"), variant: "tgt" }],
        targetValue: getTargetValue("time-to-activation"),
      },
      {
        id: "total-scheduled-hours",
        title: "Total Scheduled Hours",
        tooltip: leadingKpiTips["total-scheduled-hours"],
        value: totalHoursStr,
        valueUnit: "hrs",
        tone: totalHours >= 800 ? "green" : "red",
        badge: totalHours >= 800 ? "on" : "off",
        compare: mkCompare(totalHoursPrevStr, totalHoursStr),
        sparkline: data?.totalScheduledHoursSparkline ?? [],
        sparklineLabels: last6SparklineLabels,
        subRows: [{ label: "Target", value: getTarget("total-scheduled-hours"), variant: "tgt" }],
        targetValue: getTargetValue("total-scheduled-hours"),
      },
      {
        id: "total-new-users",
        title: "Total New Users",
        tooltip: leadingKpiTips["total-new-users"],
        value: totalUsersStr,
        tone: "gray",
        badge: "na",
        compare: mkCompare(
          totalUsersPrevStr,
          data?.totalNewUsersAvg6Wk != null
            ? Math.round(data.totalNewUsersAvg6Wk).toString()
            : avgUsersPerTeamStr
        ),
        sparkline: data?.totalNewUsersSparkline ?? [],
        sparklineLabels: last6SparklineLabels,
        subRows: [{ label: "Target", value: getTarget("total-new-users"), variant: "tgt" }],
        targetValue: getTargetValue("total-new-users"),
      },
    ];
  })();

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSectionOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        const newOrder = sanitizeSectionOrder(arrayMove(items, oldIndex, newIndex));

        // Persist to localStorage
        localStorage.setItem("plh-dashboard-order", JSON.stringify(newOrder));

        return newOrder;
      });
    }
  };

  // Derive diagnosis from PLH data
  const diagnosisFiredRules: string[] = [];
  const diagnosisQuietRules: string[] = [];
  if (data) {
    const actRate = data.funnel?.activationRatePercent ?? 0;
    if (actRate < 5) diagnosisFiredRules.push("Activation Rate < 5%");
    if (data.timeToActivation == null) diagnosisFiredRules.push("TTFS unmeasured");
    if ((data.activatedToPaid ?? 0) === 0) diagnosisFiredRules.push("Zero paid conversions");
    if ((data.totalScheduledHours ?? 0) >= (data.totalScheduledHoursPrev ?? 0) && data.totalScheduledHoursPrev != null)
      diagnosisQuietRules.push("Scheduled Hours growing ✓");
    if ((data.totalNewUsers ?? 0) >= (data.totalNewUsersPrev ?? 0) && data.totalNewUsersPrev != null)
      diagnosisQuietRules.push("Team size expanding ✓");
  }
  const diagnosisBody =
    data && (data.activatedToPaid ?? 0) === 0 && (data.newSignups?.count ?? 0) > 0
      ? (<>Zero conversions to paid. {(data.newSignups?.count ?? 0)} signups entered but none converted. Biggest bleed: <b>Signup → Setup</b> — fix onboarding first.</>)
      : (<>Review your funnel and KPIs above. Focus on the highest-impact lever first.</>);

  // Render individual sections
  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "ai-brief":
        return (
          <AiBriefTile
            text={
              <>
                <b>Week focus:</b> Fix the signup→setup bleed — that's your #1 lever. Focus on onboarding, conversion calls, and tracking. <b>Let's go team!</b>
              </>
            }
          />
        );
      case "diagnosis":
        return (
          <DiagnosisTile
            firedCount={diagnosisFiredRules.length}
            totalRules={5}
            body={diagnosisBody}
            firedRules={diagnosisFiredRules}
            quietRules={diagnosisQuietRules}
          />
        );
      case "kpi-grid":
        return (
          <div className="kpi-section">
            <div className="sec-hdr">Leading Indicators</div>
            <div className="kpi-grid">
              {kpiCards
                .filter((card) => PLH_KPI_VISIBILITY[card.id] !== false)
                .map((card) => (
                  <KPICard
                    key={card.id}
                    {...card}
                    onClick={() => setSelectedKpiCard(card)}
                  />
                ))}
            </div>
          </div>
        );
      case "activation-funnel":
        return (
          <ActivationFunnelTile
            funnel={funnel}
            activatedToPaid={data?.activatedToPaid || 0}
            activatedToPaidHistory={data?.activatedToPaidHistory}
            paidFromActivatedCohortHistory={data?.paidFromActivatedCohortHistory}
            selectedWeek={data?.meta?.selectedWeek}
            prevPeriodLabel={
              granularity === "month" &&
              data?.meta?.selectedCalendarYear != null &&
              data?.meta?.selectedMonth != null
                ? previousMonthShort(data.meta.selectedCalendarYear, data.meta.selectedMonth)
                : undefined
            }
            funnelTooltipExtra={
              granularity === "month"
                ? "Monthly view: funnel counts are calendar-month sums of weekly signup-cohort rows; activation rate = activated ÷ ICP signups. Unique visits are summed weekly session-uniques (not deduplicated across weeks)."
                : undefined
            }
            periodKind={granularity}
            goalOverrides={funnelStepGoals}
            onStepClick={(payload) => setFunnelStepPanel(payload)}
          />
        );
      case "funnel-trend":
        return (
          <FunnelTrendTile funnelTrend={data?.funnelTrend} periodKind={granularity} />
        );
      case "missions":
        return (
          <MissionsTile
            completedSet={missionCompletedSet}
            missionNotes={missionNotes}
            onToggleDone={(i) => {
              setMissionCompletedSet((prev) => {
                const next = new Set(prev);
                if (next.has(i)) next.delete(i);
                else next.add(i);
                return next;
              });
            }}
            onMissionClick={setSelectedMissionIndex}
          />
        );
      case "icp-analysis":
        return (
          <IcpAnalysisTile
            targetIcp={icpTarget}
            onEditIcp={() => setSelectedPanel("icp")}
            onOpenSignupProfile={() => setSelectedPanel("signupProfile")}
          />
        );
      case "new-signups":
        return (
          <NewSignupsTile
            signups={data?.newSignups?.signups || []}
            count={data?.newSignups?.count || 0}
          />
        );
      case "activation-variables":
        return (
          <ActivationVariablesTile
            activationParameters={data?.activationParameters}
          />
        );
      default:
        return null;
    }
  };

  if (loading && !data) {
    return (
      <div className="plh-dashboard" data-theme={theme} style={{ minHeight: "100%" }}>
        <div className="plh-loading">
          <div>Loading PLH Metrics…</div>
          <div className="plh-loading__message">
            This may take more than 60 seconds if PostHog is materializing.
          </div>
          <div className="plh-loading__progress-track" role="progressbar" aria-valuetext="Loading">
            <div className="plh-loading__progress-bar" />
          </div>
        </div>
      </div>
    );
  }
  if (error) return <div style={{ padding: 32, color: 'red' }}>Error loading metrics: {error}</div>;

  return (
    <div
      className="plh-dashboard"
      data-theme={theme}
      {...css({
        maxWidth: 1400,
        margin: "0 auto",
        padding: "32px 16px 64px 16px",
        fontFamily: "Museo Sans, Futura, Trebuchet MS, Arial, sans-serif",
        minHeight: "100%",
      })}
    >
      <TopBarTile
        meta={data?.meta}
        granularity={granularity}
        onGranularityChange={handleGranularityChange}
        selectedWeekParam={
          weekParam ??
          (data?.meta?.selectedWeek != null && data?.meta?.selectedYear != null
            ? `${data.meta.selectedYear}-W${String(data.meta.selectedWeek).padStart(2, "0")}`
            : null)
        }
        onWeekChange={setWeekParam}
        selectedMonthParam={
          monthParam ??
          (data?.meta?.selectedCalendarYear != null && data?.meta?.selectedMonth != null
            ? `${data.meta.selectedCalendarYear}-${String(data.meta.selectedMonth).padStart(2, "0")}`
            : null)
        }
        onMonthChange={setMonthParam}
        aiCoachOpen={aiCoachOpen}
        onAiCoachToggle={() => setAiCoachOpen((v) => !v)}
        theme={theme}
        onThemeToggle={onThemeToggle}
        onOpenSettings={
          plhView === "dashboard"
            ? () => {
                closePanel();
                setPlhView("settings");
              }
            : undefined
        }
        showPeriodControls={plhView === "dashboard"}
      />

      {plhView === "settings" ? (
        <>
          <div
            {...css({
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            })}
          >
            <button
              type="button"
              className="plh-text-btn"
              onClick={() => setPlhView("dashboard")}
              aria-label="Back to dashboard"
            >
              ← Dashboard
            </button>
            <h2
              {...css({
                fontSize: 18,
                fontWeight: 600,
                margin: 0,
                color: "var(--t1)",
              })}
            >
              Settings
            </h2>
          </div>
          <MaterializationStatusTile views={data?.materializationStatus} />
          <PostHogEndpointCacheTile
            freshness={data?.posthogEndpointFreshness}
            loading={loading}
            onRequestForce={(id) => void loadPlh({ posthogForce: [id] })}
            onRequestForceAll={() => void loadPlh({ posthogForce: ["all"] })}
          />
        </>
      ) : (
        <>
      {/* Sortable Dashboard Sections */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sectionOrder.filter((id) => PLH_SECTION_VISIBILITY[id] !== false)}
          strategy={verticalListSortingStrategy}
        >
          {sectionOrder
            .filter((id) => PLH_SECTION_VISIBILITY[id] !== false)
            .map((sectionId) => (
              <SortableSection key={sectionId} id={sectionId}>
                {renderSection(sectionId)}
              </SortableSection>
            ))}
        </SortableContext>
      </DndContext>

      {/* Sliding panel (KPI or Funnel step) */}
      <div
        className={`plh-overlay ${isPanelOpen ? "open" : ""}`}
        onClick={closePanel}
        aria-hidden={!isPanelOpen}
      />
      <div
        className={`plh-panel ${isPanelOpen ? "open" : ""}`}
        aria-hidden={!isPanelOpen}
      >
        {funnelStepPanel != null && (
          <>
            <div className="plh-panel-hdr">
              <h3>{funnelStepPanel.title}</h3>
              <button
                ref={panelCloseRef}
                type="button"
                className="plh-panel-close"
                onClick={closeFunnelStepPanel}
                aria-label="Close panel"
              >
                ✕
              </button>
            </div>
            <div className="plh-panel-body">
              <FunnelStepPanelBody
                payload={funnelStepPanel}
                goal={funnelStepGoals[funnelStepPanel.stepKey] ?? funnelStepPanel.goal ?? 0}
                goalMode={funnelStepGoalModes[funnelStepPanel.stepKey] ?? "absolute"}
                onGoalModeChange={(mode) => setFunnelStepGoalModes((prev) => ({ ...prev, [funnelStepPanel.stepKey]: mode }))}
                prevStepActual={funnelStepPanel.prevStepActual ?? null}
                prevStepGoal={funnelStepPanel.prevStepGoal ?? null}
                percentModeLabel={funnelStepPanel.percentModeLabel}
                onSaveGoal={(value, note) => {
                  setFunnelStepGoals((prev) => ({ ...prev, [funnelStepPanel.stepKey]: value }));
                  setFunnelStepEditHistory((prev) => ({
                    ...prev,
                    [funnelStepPanel.stepKey]: [
                      { who: "You", value, when: "Just now", ...(note ? { note } : {}) },
                      ...(prev[funnelStepPanel.stepKey] ?? []),
                    ].slice(0, 5),
                  }));
                  closeFunnelStepPanel();
                }}
                editHistory={funnelStepEditHistory[funnelStepPanel.stepKey] ?? []}
              />
            </div>
          </>
        )}
        {funnelStepPanel == null && selectedMissionIndex == null && selectedPanel === "icp" && (
          <>
            <div className="plh-panel-hdr">
              <h3>🎯 Edit ICP Definition</h3>
              <button
                ref={panelCloseRef}
                type="button"
                className="plh-panel-close"
                onClick={closeIcpPanel}
                aria-label="Close panel"
              >
                ✕
              </button>
            </div>
            <div className="plh-panel-body">
              <IcpEditorPanelBody
                targetIcp={icpTarget}
                onSave={(next) => {
                  setIcpTarget(next);
                  closeIcpPanel();
                }}
                onCancel={closeIcpPanel}
              />
            </div>
          </>
        )}
        {funnelStepPanel == null && selectedMissionIndex == null && selectedPanel === "signupProfile" && (
          <>
            <div className="plh-panel-hdr">
              <h3>📊 Actual Signup Profile</h3>
              <button
                ref={panelCloseRef}
                type="button"
                className="plh-panel-close"
                onClick={closeIcpPanel}
                aria-label="Close panel"
              >
                ✕
              </button>
            </div>
            <div className="plh-panel-body">
              <SignupProfilePanelBody />
            </div>
          </>
        )}
        {funnelStepPanel == null && selectedMissionIndex != null && (
          <>
            <div className="plh-panel-hdr">
              <h3>
                {PLH_MISSIONS_MOCK[selectedMissionIndex]?.icon}{" "}
                {PLH_MISSIONS_MOCK[selectedMissionIndex]?.title}
              </h3>
              <button
                ref={panelCloseRef}
                type="button"
                className="plh-panel-close"
                onClick={closeMissionPanel}
                aria-label="Close panel"
              >
                ✕
              </button>
            </div>
            <div className="plh-panel-body">
              <MissionPanelBody
                mission={PLH_MISSIONS_MOCK[selectedMissionIndex]!}
                missionIndex={selectedMissionIndex}
                isDone={missionCompletedSet.has(selectedMissionIndex)}
                notes={missionNotes[selectedMissionIndex] ?? ""}
                onNotesChange={(value) =>
                  setMissionNotes((prev) => ({ ...prev, [selectedMissionIndex]: value }))
                }
                onToggleDone={() => {
                  setMissionCompletedSet((prev) => {
                    const next = new Set(prev);
                    if (next.has(selectedMissionIndex)) next.delete(selectedMissionIndex);
                    else next.add(selectedMissionIndex);
                    return next;
                  });
                }}
              />
            </div>
          </>
        )}
        {funnelStepPanel == null && selectedMissionIndex == null && selectedPanel == null && selectedKpiCard != null && (
          <>
            <div className="plh-panel-hdr">
              <h3>{selectedKpiCard.title}</h3>
              <button
                ref={panelCloseRef}
                type="button"
                className="plh-panel-close"
                onClick={closeKpiPanel}
                aria-label="Close panel"
              >
                ✕
              </button>
            </div>
            <div className="plh-panel-body">
              <KpiPanelBody
                card={selectedKpiCard}
                periodKind={granularity}
                goalOverride={kpiGoals[selectedKpiCard.id]}
                editHistory={kpiEditHistory[selectedKpiCard.id] ?? []}
                onSaveGoal={(value) => {
                  setKpiGoals((prev) => ({ ...prev, [selectedKpiCard.id]: value }));
                  setKpiEditHistory((prev) => ({
                    ...prev,
                    [selectedKpiCard.id]: [
                      { who: "You", value, when: "Just now" },
                      ...(prev[selectedKpiCard.id] ?? []),
                    ].slice(0, 5),
                  }));
                }}
              />
            </div>
          </>
        )}
      </div>

      <AiCoachChatPanel open={aiCoachOpen} onClose={() => setAiCoachOpen(false)} />
        </>
      )}
    </div>
  );
}

/** ICP editor panel: form fields, Save/Cancel, edit history, chat. */
function IcpEditorPanelBody({
  targetIcp,
  onSave,
  onCancel,
}: {
  targetIcp: IcpTarget;
  onSave: (next: IcpTarget) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<IcpTarget>(targetIcp);

  const fields: (keyof IcpTarget)[] = [
    "industry",
    "teamSize",
    "currentTool",
    "decisionMaker",
    "painSignal",
    "geography",
  ];
  const labels: Record<keyof IcpTarget, string> = {
    industry: "Industry",
    teamSize: "Team Size",
    currentTool: "Current Tool",
    decisionMaker: "Decision Maker",
    painSignal: "Pain Signal",
    geography: "Geography",
  };

  return (
    <>
      <div style={{ fontSize: 13, color: "var(--t3)", marginBottom: 16 }}>
        Adjusting your ICP updates the match scoring for all signups.
      </div>
      {fields.map((key) => (
        <div key={key} className="sf-group" style={{ marginBottom: 14 }}>
          <label className="sf-label" style={{ fontSize: 11 }}>
            {labels[key]}
          </label>
          <input
            type="text"
            className="sf-input"
            style={{ fontSize: 13, padding: "8px 0" }}
            value={form[key]}
            onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
          />
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button
          type="button"
          className="sf-btn sf-btn-primary"
          onClick={() => onSave(form)}
          style={{ padding: "9px 22px", borderRadius: 8, border: "none", background: "var(--soon-blue)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          Save ICP
        </button>
        <button
          type="button"
          className="sf-btn sf-btn-secondary"
          onClick={onCancel}
          style={{ padding: "9px 22px", borderRadius: 8, background: "var(--s3)", color: "var(--t3)", fontSize: 13, fontWeight: 600, cursor: "pointer", border: "1px solid var(--brd)" }}
        >
          Cancel
        </button>
      </div>
      <div className="edit-log" style={{ marginTop: 16 }}>
        <div className="el-title">Edit History</div>
        <div className="el-item">
          <span className="el-who">Alessandro</span> updated Industry <span className="el-when">Feb 1</span>
        </div>
        <div className="el-item">
          <span className="el-who">Set by AI</span> initial ICP <span className="el-when">Jan 15</span>
        </div>
      </div>
      {PLH_INLINE_CHAT_VISIBILITY["icp-editor"] !== false && (
        <InlineChatBlock
          title="✦ Ask AI about ICP"
          initialMessage="I can analyze your signup data vs ICP, suggest adjustments, or identify emerging segments."
          placeholder="e.g. Should we lower team size to 5?"
          contextLabel="ICP"
        />
      )}
    </>
  );
}

/** Signup Profile panel: intro, breakdown, metrics, AI analysis, chat. */
function SignupProfilePanelBody() {
  return (
    <>
      <div className="fc-enrich" style={{ marginBottom: 16 }}>
        <div className="fc-hdr">🔥 Powered by Firecrawl</div>
        <div style={{ fontSize: 12, color: "var(--t3)" }}>Enrichment data from signups this week</div>
      </div>
      <div className="p-section-title">Industry Breakdown</div>
      <div style={{ marginBottom: 14 }}>
        {["Healthcare", "Events", "Childcare", "Retail", "Other"].map((label, i) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "var(--t2)", width: 140 }}>{label}</span>
            <div style={{ flex: 1, height: 8, background: "var(--bar-bg)", borderRadius: 4, overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${[29, 21, 14, 12, 24][i]}%`,
                  background: "var(--soon-blue)",
                  borderRadius: 4,
                }}
              />
            </div>
            <span className="mono" style={{ fontSize: 11, color: "var(--t3)", width: 36, textAlign: "right" }}>
              {[29, 21, 14, 12, 24][i]}%
            </span>
          </div>
        ))}
      </div>
      <div className="p-section-title">Key Metrics</div>
      <div className="p-grid" style={{ marginBottom: 14 }}>
        <div className="p-stat">
          <div className="ps-label">Avg Team Size</div>
          <div className="ps-val">6.2</div>
        </div>
        <div className="p-stat">
          <div className="ps-label">ICP Match Rate</div>
          <div className="ps-val" style={{ color: "var(--amber-t)" }}>50%</div>
        </div>
        <div className="p-stat">
          <div className="ps-label">ICP Activation</div>
          <div className="ps-val" style={{ color: "var(--green-t)" }}>67%</div>
        </div>
        <div className="p-stat">
          <div className="ps-label">Non-ICP Activation</div>
          <div className="ps-val" style={{ color: "var(--red-t)" }}>12%</div>
        </div>
      </div>
      <div className="icp-insight" style={{ marginBottom: 14 }}>
        <div className="ii-hdr">✦ AI Analysis</div>
        <p>
          ICP users activate 5.6× better than non-ICP. Your avg team size (6.2) is below the ≥8 threshold. Top performing verticals: Healthcare and Events. Consider tightening signup qualification — half your funnel is non-ICP traffic.
        </p>
      </div>
      {PLH_INLINE_CHAT_VISIBILITY["signup-profile"] !== false && (
        <InlineChatBlock
          title="✦ Explore with AI"
          initialMessage="I can compare cohorts, find emerging segments, or suggest ICP changes based on who actually converts. What interests you?"
          placeholder="e.g. Which industries convert best?"
          contextLabel="Signup Profile"
        />
      )}
    </>
  );
}

/** Mission detail panel: detail, status, notes, refine, chat. */
function MissionPanelBody({
  mission,
  isDone,
  notes,
  onNotesChange,
  onToggleDone,
}: {
  mission: MissionItem;
  missionIndex: number;
  isDone: boolean;
  notes: string;
  onNotesChange: (value: string) => void;
  onToggleDone: () => void;
}) {
  const diffLabel = mission.diff === "easy" ? "Easy" : mission.diff === "med" ? "Medium" : "Hard";
  const impactDots = Array.from({ length: 5 }, (_, j) => (
    <span key={j} className={j < mission.impact ? "on" : ""} />
  ));

  return (
    <>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <span className="m-xp" style={{ fontSize: 11, padding: "3px 8px" }}>
          +{mission.xp} XP
        </span>
        <span className={`m-diff ${mission.diff}`} style={{ fontSize: 11, padding: "3px 8px" }}>
          {diffLabel}
        </span>
        <span style={{ fontSize: 10, color: "var(--t4)", display: "flex", alignItems: "center", gap: 4 }}>
          Impact <span className="m-impact-dots">{impactDots}</span>
        </span>
      </div>
      <div style={{ fontSize: 13, color: "var(--t2)", lineHeight: 1.6, marginBottom: 16 }}>
        {mission.detail}
      </div>
      <div style={{ fontSize: 11, color: "var(--t4)", marginBottom: 14 }}>
        Target metric: <b style={{ color: "var(--t2)" }}>{mission.metric}</b>
      </div>
      <div className="p-section-title">Status</div>
      <div className="mp-status">
        <button
          type="button"
          className={`mp-s-btn${isDone ? " sel" : ""}`}
          onClick={() => {
            onToggleDone();
          }}
        >
          ✓ Done
        </button>
        <button type="button" className="mp-s-btn">
          ◐ In Progress
        </button>
        <button type="button" className="mp-s-btn">
          ↷ Skipped
        </button>
      </div>
      <div className="p-section-title">Notes & Context for AI</div>
      <textarea
        className="mp-notes"
        placeholder="Add context: what happened, what you tried, blockers… This helps the AI generate better missions next week."
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
      />
      <button
        type="button"
        className="mp-refine"
        onClick={() => alert("In production, this would call the AI to rewrite the mission based on your notes and context.")}
      >
        ✦ Refine this mission with AI
      </button>
      {PLH_INLINE_CHAT_VISIBILITY["mission-detail"] !== false && (
        <div style={{ marginTop: 14 }}>
          <InlineChatBlock
            title="✦ Discuss with AI"
            initialMessage="I can help you break this down, estimate impact, or adjust the approach. What do you need?"
            placeholder="e.g. How should I prioritize this?"
            contextLabel={mission.title}
          />
        </div>
      )}
    </>
  );
}

/** Funnel step panel: hero, goal setting, edit history, inline chat. */
function FunnelStepPanelBody({
  payload,
  goal,
  goalMode,
  onGoalModeChange,
  prevStepActual,
  prevStepGoal,
  percentModeLabel,
  onSaveGoal,
  editHistory,
}: {
  payload: FunnelStepPanelPayload;
  goal: number;
  goalMode: "absolute" | "percent";
  onGoalModeChange: (mode: "absolute" | "percent") => void;
  prevStepActual: number | null;
  prevStepGoal: number | null;
  percentModeLabel?: string;
  onSaveGoal: (value: number, note?: string) => void;
  editHistory: Array<{ who: string; value: number; when: string; note?: string }>;
}) {
  const baseForPercent = percentModeLabel ? prevStepActual : (prevStepGoal ?? prevStepActual);
  const isPercentModeAvailable = baseForPercent != null;
  const isPercentModeUsable = baseForPercent != null && baseForPercent > 0;
  const effectiveMode = isPercentModeUsable ? goalMode : "absolute";

  const computeInitialInputValue = useCallback(() => {
    const currentGoal = payload.goal ?? goal;
    if (effectiveMode === "percent" && baseForPercent != null && baseForPercent > 0) {
      return String(Math.round((currentGoal / baseForPercent) * 100));
    }
    return String(currentGoal);
  }, [payload.goal, goal, effectiveMode, baseForPercent]);

  const [goalInput, setGoalInput] = useState(computeInitialInputValue);

  useEffect(() => {
    setGoalInput(computeInitialInputValue());
  }, [computeInitialInputValue, payload.stepKey]);

  const handleSave = () => {
    const v = parseInt(goalInput, 10);
    if (Number.isNaN(v) || v < 0) return;

    if (effectiveMode === "percent" && isPercentModeUsable) {
      const base = percentModeLabel ? prevStepActual! : (prevStepGoal ?? prevStepActual!);
      const absoluteValue = Math.round((base * v) / 100);
      onSaveGoal(absoluteValue, `${v}% of ${percentModeLabel ? "prev week" : "prev step"}`);
    } else {
      onSaveGoal(v);
    }
  };

  const hit = payload.hit;

  const computedAbsoluteFromPercent =
    effectiveMode === "percent" && isPercentModeUsable
      ? Math.round(((percentModeLabel ? prevStepActual! : (prevStepGoal ?? prevStepActual!)) * parseInt(goalInput, 10)) / 100)
      : null;

  return (
    <>
      <div className="plh-kpi-hero">
        <div>
          <div className="plh-kpi-hero-value mono">
            {typeof payload.actual === "number" ? payload.actual.toLocaleString() : payload.actual}
          </div>
          <div className="plh-kpi-hero-status">{payload.subtitle}</div>
        </div>
        {payload.vsPct != null && (
          <div className={`f-vs ${hit ? "hit" : "miss"}`}>
            {payload.vsPct}% of goal {hit ? "✓" : "✗"}
          </div>
        )}
      </div>
      <div className="p-grid">
        <div className="p-stat">
          <div className="ps-label">{payload.prevWeekLabel ?? "Prev wk"}</div>
          <div className="ps-val mono">
            {payload.prevWeekValue != null ? payload.prevWeekValue.toLocaleString() : "—"}
          </div>
        </div>
        <div className="p-stat">
          <div className="ps-label">6-wk Avg</div>
          <div className="ps-val mono">
            {payload.avg6WeekValue != null ? payload.avg6WeekValue.toLocaleString() : "—"}
          </div>
        </div>
      </div>
      <div className="p-section-title">Description</div>
      <p className="plh-kpi-detail" style={{ marginTop: 0 }}>
        {(PLH_FUNNEL_STEP_DESCRIPTION as Record<string, string>)[payload.stepKey] ?? "—"}
      </p>
      <div className="p-section-title">Goal Setting</div>
      {isPercentModeAvailable && (
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          <button
            type="button"
            className={`mp-s-btn${effectiveMode === "absolute" ? " sel" : ""}`}
            onClick={() => onGoalModeChange("absolute")}
            style={{ fontSize: 11, padding: "5px 12px", borderRadius: 6, border: `1px solid ${effectiveMode === "absolute" ? "var(--soon-blue)" : "var(--brd2)"}`, background: effectiveMode === "absolute" ? "var(--soon-blue)" : "var(--s2)", color: effectiveMode === "absolute" ? "#fff" : "var(--t2)", cursor: "pointer", fontWeight: 500 }}
          >
            Absolute (#)
          </button>
          <button
            type="button"
            className={`mp-s-btn${effectiveMode === "percent" ? " sel" : ""}`}
            disabled={!isPercentModeUsable}
            title={!isPercentModeUsable ? `Cannot use percentage mode: ${percentModeLabel ? "previous week" : "previous step"} value is 0` : undefined}
            onClick={isPercentModeUsable ? () => onGoalModeChange("percent") : undefined}
            style={{ fontSize: 11, padding: "5px 12px", borderRadius: 6, border: `1px solid ${effectiveMode === "percent" ? "var(--soon-blue)" : "var(--brd2)"}`, background: effectiveMode === "percent" ? "var(--soon-blue)" : "var(--s2)", color: !isPercentModeUsable ? "var(--t4)" : effectiveMode === "percent" ? "#fff" : "var(--t2)", cursor: isPercentModeUsable ? "pointer" : "not-allowed", fontWeight: 500, opacity: isPercentModeUsable ? 1 : 0.5 }}
          >
            {percentModeLabel ?? "% of previous step"}
          </button>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "var(--t3)" }}>Target:</span>
        <input
          type="number"
          value={goalInput}
          onChange={(e) => setGoalInput(e.target.value)}
          min={0}
          max={effectiveMode === "percent" ? 999 : undefined}
          className="plh-panel-goal-input"
          style={{ width: 80, padding: "6px 10px", border: "1px solid var(--brd2)", borderRadius: 6, background: "var(--s2)", color: "var(--t1)", fontFamily: "var(--mono)", fontSize: 14, fontWeight: 700 }}
        />
        <span style={{ fontSize: 12, color: "var(--t4)" }}>
          {effectiveMode === "percent" && baseForPercent
            ? percentModeLabel
              ? `${percentModeLabel.replace("% of ", "")} (${baseForPercent})${!Number.isNaN(parseInt(goalInput, 10)) ? ` = ${computedAbsoluteFromPercent}` : ""}`
              : `% of ${baseForPercent}${!Number.isNaN(parseInt(goalInput, 10)) ? ` = ${computedAbsoluteFromPercent}` : ""}`
            : "absolute"}
        </span>
        <button type="button" onClick={handleSave} className="plh-panel-save-btn" style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--soon-blue)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Save
        </button>
      </div>
      <div className="edit-log">
        <div className="el-title">Edit History</div>
        {editHistory.length > 0
          ? editHistory.map((entry, i) => (
              <div key={i} className="el-item">
                <span className="el-who">{entry.who}</span> set goal to {entry.value}{entry.note ? ` (${entry.note})` : ""} <span className="el-when">{entry.when}</span>
              </div>
            ))
          : (
              <div className="el-item">
                <span className="el-who">Set by AI</span> initial target <span className="el-when">—</span>
              </div>
            )}
      </div>
      {PLH_INLINE_CHAT_VISIBILITY["funnel-step"] !== false && (
        <InlineChatBlock
          title={`✦ Ask AI about ${payload.title}`}
          initialMessage={`What would you like to know about ${payload.title}? I can suggest targets, explain trends, or recommend experiments.`}
          placeholder="e.g. Is this target realistic?"
          contextLabel={payload.title}
        />
      )}
    </>
  );
}

/** KPI panel body: hero, p-grid, weekly trend, goal setting, edit history, what this means, recommended actions, AI chat. */
function KpiPanelBody({
  card,
  periodKind = "week",
  goalOverride,
  editHistory = [],
  onSaveGoal,
}: {
  card: KpiCardConfig;
  periodKind?: "week" | "month";
  goalOverride?: string;
  editHistory?: Array<{ who: string; value: string; when: string }>;
  onSaveGoal?: (value: string) => void;
}) {
  const defaultTarget = card.subRows?.find((r) => r.variant === "tgt")?.value ?? "—";
  const targetValue = goalOverride ?? defaultTarget;
  const statusLabel = card.badge === "off" ? "Off Track" : card.badge === "on" ? "On Track" : "—";
  const isHit = card.badge === "on";
  const detailSource = periodKind === "month" ? PLH_KPI_DETAIL_MONTH : PLH_KPI_DETAIL;
  const detail = (detailSource as Record<string, string>)[card.id] ?? card.tooltip ?? "";
  const actions = (PLH_KPI_ACTIONS as Record<string, string>)[card.id] ?? "";

  const [goalInput, setGoalInput] = useState(targetValue);
  useEffect(() => {
    setGoalInput(targetValue);
  }, [card.id, targetValue]);

  const isEditableTarget = onSaveGoal && defaultTarget !== "—" && card.id !== "time-to-activation";

  const handleSaveKpiGoal = () => {
    const v = goalInput.trim();
    if (v && onSaveGoal) onSaveGoal(v);
  };

  // Weekly trend: 6 bars from sparkline or placeholder
  const weeklyValues = card.sparkline && card.sparkline.length >= 6
    ? card.sparkline.slice(0, 6)
    : [0, 0, 0, 0, 0, 0];
  const maxWeekly = Math.max(...weeklyValues, 1);
  const barHeight = (v: number) => Math.max((v / maxWeekly) * 50, 3);

  // Hover state for weekly trend bars (defaults to the latest week when not hovering)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  useEffect(() => {
    // Reset hover state when switching KPI cards
    setHoverIndex(null);
  }, [card.id]);

  const hasWeeklyData = weeklyValues.length > 0;
  const effectiveIndex = hasWeeklyData ? hoverIndex ?? weeklyValues.length - 1 : null;
  const hoveredValue = effectiveIndex != null ? weeklyValues[effectiveIndex] : null;
  const hoveredLabel =
    effectiveIndex != null
      ? (card.sparklineLabels && card.sparklineLabels[effectiveIndex]) || `W${effectiveIndex + 1}`
      : null;
  const hoveredDisplay =
    hoveredValue != null ? `${hoveredValue}${card.valueUnit ?? ""}` : "—";

  return (
    <>
      <div className="plh-kpi-hero">
        <div>
          <div className="plh-kpi-hero-value mono">
            {card.value}
            {card.valueUnit != null && <span className="u">{card.valueUnit}</span>}
          </div>
          <div className="plh-kpi-hero-status">{statusLabel}</div>
        </div>
        <div className={`f-vs ${isHit ? "hit" : "miss"}`}>
          Target: {targetValue}
        </div>
      </div>
      {card.compare != null && (
        <div className="p-grid">
          <div className="p-stat">
            <div className="ps-label">{card.compare.prevLabel}</div>
            <div className="ps-val mono">{card.compare.wk5.value}</div>
          </div>
          <div className="p-stat">
            <div className="ps-label">6-wk Avg</div>
            <div className="ps-val mono">{card.compare.avg}</div>
          </div>
        </div>
      )}
      {isEditableTarget && (
        <>
          <div className="p-section-title">Goal Setting</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "var(--t3)" }}>Target:</span>
            <input
              type="text"
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              className="plh-panel-goal-input"
              style={{ width: 100, padding: "6px 10px", border: "1px solid var(--brd2)", borderRadius: 6, background: "var(--s2)", color: "var(--t1)", fontFamily: "var(--mono)", fontSize: 14 }}
            />
            <button type="button" onClick={handleSaveKpiGoal} className="plh-panel-save-btn" style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--soon-blue)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              Save
            </button>
          </div>
        </>
      )}
      <div className="p-section-title">Weekly Trend</div>
      {hoveredLabel && (
        <div className="plh-kpi-weekly-hover mono">
          {hoveredLabel}: {hoveredDisplay}
        </div>
      )}
      <div className="plh-kpi-weekly-bars">
        {weeklyValues.map((v, j) => (
          <div
            key={j}
            className="plh-kpi-weekly-bar-wrap"
            onMouseEnter={() => setHoverIndex(j)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <div
              className={`plh-kpi-weekly-bar${hoverIndex === j ? " hovered" : ""}`}
              style={{
                height: barHeight(v),
                background: j === 5 ? "var(--soon-blue)" : "var(--s3)",
              }}
            />
            <span className="plh-kpi-weekly-label mono">
              {card.sparklineLabels && card.sparklineLabels[j]
                ? card.sparklineLabels[j]
                : `W${j + 1}`}
            </span>
          </div>
        ))}
      </div>
      <div className="p-section-title">What this means</div>
      <p className="plh-kpi-detail">{detail}</p>
      <div className="p-section-title">Recommended Actions</div>
      <div className="plh-kpi-actions">{actions}</div>
      <div className="edit-log">
        <div className="el-title">Edit History</div>
        {editHistory.length > 0
          ? editHistory.map((entry, i) => (
              <div key={i} className="el-item">
                <span className="el-who">{entry.who}</span> set target to {entry.value} <span className="el-when">{entry.when}</span>
              </div>
            ))
          : (
              <div className="el-item">
                <span className="el-who">Set by AI</span> set target to {targetValue} <span className="el-when">—</span>
              </div>
            )}
      </div>
      {PLH_INLINE_CHAT_VISIBILITY["kpi-detail"] !== false && (
        <InlineChatBlock
          title={`✦ Discuss with AI`}
          initialMessage={`I can help you understand ${card.title} trends, suggest target adjustments, or recommend experiments to improve it. What do you need?`}
          placeholder="e.g. Is this target realistic?"
          contextLabel={card.title}
        />
      )}
    </>
  );
}
